import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'

import { Observable } from 'rxjs'

import { environment } from '../../../environments/environment'
import { Page } from '../../core/models/page'

import { uuid } from '../../core/helpers/uuid'
import { urlSafe } from '../../core/helpers/url-ready'
import { UXPage } from '../../core/models/ux-page'
import { compareNumber } from '../../core/helpers/sorting';
import { ResponseWrap } from '../../core/models/response-wrap';

const pagesUrl = '/pages'

@Injectable()
export class PagesService {
    constructor(
        private http: HttpClient
    ){}

    getPages(customerId: string): Observable<UXPage[]> {
      const params = new HttpParams().set('customerId', customerId)

        return this.http.get<ResponseWrap<Page>>(
            environment.baseApiUrl + pagesUrl,
            { params }
        ).pipe(map(res => this.preparePages(res.results)))
    }

    private preparePages(pages: Page[]): UXPage[] {
      let topLevel = uuid()

        let parentBased = pages.reduce(
            (dict, page) => Object.assign(
                dict,
                {
                    [page.parent || topLevel]: [
                        ...(dict[page.parent || topLevel] || []),
                        page
                    ]
                }
            ), {})

        let lookup = {}
        let hierarchical: Page[] = parentBased[topLevel]
            .map(this.getChildren.bind(this, '', parentBased, lookup))
            .sort(compareNumber<Page>(page => page.sortOrder))

        return hierarchical.map(this.toUXPage.bind(this, lookup))
    }

    private getChildren(
        prevUrl: string,
        parentBased: { [parent: string]: Page[] },
        lookup: {[_id:string]:Page},
        current: Page
    ): Page {
        let urlCode = urlSafe(current.directory)
        let url = `${prevUrl}/${urlCode}`

        Object.assign(
            current,
            {
                url: url,
                children: (parentBased[current._id] || [])
                    .map(this.getChildren.bind(this, url, parentBased, lookup))
                    .sort(compareNumber<Page>(page => page.sortOrder))
            }
        )

        return lookup[current._id] = current
    }

    private toUXPage(lookup: { [_id:string]: Page }, page: Page, index, pages: Page[]): UXPage {
        return {
            id: page._id,
            code: page.code,
            type: page.type,
            menu: page.menu,
            name: page.name,
            introText: page.introText,
            slug: page.slug,
            directory: page.directory,
            parameters: page.parameters,
            settings: page.settings,
            banner: page.banner ? {
                img: page.banner.img,
                title: page.banner.title,
                subTitle: page.banner.subTitle,
                buttonText: page.banner.buttonText,
                link: this.prepareUrl(pages, page.banner.linkToPage),
            } : undefined,
            blocks: (page.blocks || []).map(block => ({
                img: block.img,
                title: block.title,
                subTitle: block.subTitle,
                buttonText: block.buttonText,
                link: this.prepareUrl(pages, block.linkToPage),
                externalLink: block.linkToUrl
            })),
            children: page.children.map(this.toUXPage.bind(this, lookup)),
            videos: page.videos,
            facetgroups: page.facetgroups,
            blocksGroups: (page.blocksGroups || []).map(blocksGroup => ({
              code: blocksGroup.code,
              title: blocksGroup.title,
              blocks: (blocksGroup.blocks || []).map(block => ({
                img: block.img,
                title: block.title,
                subTitle: block.subTitle,
                buttonText: block.buttonText,
                link: this.prepareUrl(pages, block.linkToPage),
                externalLink: block.linkToUrl
              }))
            }))
        }
    }

    prepareUrl(pages: Page[], code: string): string {
      if (Array.isArray(pages)) {
        const currentPage = pages.find(item => item.code === code) || null
        return currentPage && currentPage.slug
      }
      return ''
    }
}
